/**
 * App-side wiring for `@heidi/integrations/document-inbox/data` (C/D, ARCFE-1016).
 *
 * THE SEAM IS PACKAGE-SCOPED, NOT DOOR-SCOPED, so the specifier above names
 * where `configureIntegrationsData` lives rather than who it serves: a door that
 * lands later extends this ONE object instead of opening a second seam.
 * `./connected-apps` was the first to take that up
 * (`onOnboardingSessionCleared`), `./letters` the second (`useIsAuthenticated`);
 * the inbox's team resolution (`useTeamId`, `useTeamContext`) and
 * `./problem-charting`'s `queryClient` and `subscribeToProblemSetStream`
 * followed, which is why the two-item list below is now eight. The wiring suite
 * pins the set by EQUALITY, so a new port is a line here and a line there.
 *
 * Eight things cross the seam:
 *
 * - `loadInboxHttp` — a LOADER for the app-built client for the standalone
 *   **inbox-service**. Deliberately NOT resolved from the `@heidi/api/client`
 *   registry: that registry holds the SCRIBE client family, and this instance
 *   points at a different service (`getDomains()?.inboxServiceUrl`) with its own
 *   30s deadline, its own `X-Client-Id`/`X-Client-Version` headers and its own
 *   `Heidi-Workspace-Id` resolution. Putting it in the scribe registry would let
 *   "resolve a scribe client" return something that is not one.
 *
 *   IT IS A DYNAMIC IMPORT, AND THAT IS LOAD-BEARING — do not "simplify" it to a
 *   static import. This module is imported for effect by `_app`, so a static
 *   import would evaluate the builder's 1,187-module cone at `_app` module
 *   scope. That cone reaches the Rematch store, which is an async module under
 *   rspack/webpack; pulling it in that early reordered async-module settlement
 *   and produced `ReferenceError: Cannot access 'v' before initialization`
 *   during "Collecting page data" — on `/calls`, a page with no connection to
 *   this domain. Bisected to this exact import (ARCFE-1016). No local gate sees
 *   it: tsc, jest and lint are all blind to module-initialisation order in a
 *   bundled chunk, so only a production build catches a regression here.
 *
 *   Deferring also restores the pre-carve timing: before the move, the builder
 *   was only evaluated when a page actually used inbox data.
 *
 * - `useWorkspaceId` — the workspace door's real hook. Injected rather than
 *   imported because no package `/data` zone may value-import a foreign
 *   domain's `/data` door (this change's task 3.3). Safe to import statically:
 *   `_app` already pulls this door via `workspace-data-wiring`.
 *
 * - `onOnboardingSessionCleared` — `./connected-apps`' session-delete write
 *   resets the onboarding-session client state, which is seven per-field jotai
 *   atoms under `src/atoms/`. A package `/data` zone cannot reach them, so what
 *   crosses is the zero-arity RESET rather than the atoms: the package hook
 *   fires this port where it used to call `clearIntegrationOnboardingSession()`
 *   itself. Static import is safe for the same reason as above and one more —
 *   the atoms module imports only `jotai`, the generated schema, `@/types` and
 *   the onboarding-step encoder, so it opens no path to the store.
 *
 * - `useTeamId` / `useTeamContext` — the active team id and its resolution
 *   state, derived below from the workspace door's team query, as named hooks.
 *
 * - `queryClient` — the app's TanStack singleton, for `./problem-charting`'s
 *   module-scope invalidator (`invalidateSessionProblemSet`), which runs outside
 *   React after generate / regenerate and so cannot reach `useQueryClient()`.
 *   The same instance `coding-data-wiring.ts` injects; static import is safe
 *   because that wiring already pulls the module into the `_app` cone.
 *
 * - `subscribeToProblemSetStream` — SSE TRANSPORT for `./problem-charting`'s
 *   live pipeline feed (`GET .../problem-set/stream`). The package never learns
 *   a URL or a token: this delegate resolves the v2 domain, reads the auth
 *   token synchronously and builds the SSE headers, then hands raw events back
 *   — the same split the session package's legacy problems stream uses in
 *   `src/hooks/query/useQuerySessionProblems.ts`. Static imports are safe:
 *   `calls-data-wiring` already pulls the SSE header builder (and through it
 *   the token read) into the `_app` cone, and `getDomains` reaches no store.
 *
 * Imported at module scope in `_app.tsx` AFTER `workspace-data-wiring`, whose
 * store this hook reads.
 */
import { clearIntegrationOnboardingSession } from '@/atoms/integrationOnboardingSessionAtoms';
import { getDomain } from '@/helpers/getDomains';
import { getAuthTokenForHttpClientSync } from '@/utils/http/getAuthTokenForHttpClientSync';
import { buildScribeSseHeaders } from '@/utils/http/scribeSseHeaders';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { fetchEventSource } from '@fortaine/fetch-event-source';
import type { InboxAssigneeCandidate } from '@heidi/integrations/document-inbox/contracts';
import { configureIntegrationsData } from '@heidi/integrations/document-inbox/data';
import type { SubscribeToProblemSetStream } from '@heidi/integrations/problem-charting/contracts';
import { formatCjkAwareDisplayName } from '@heidi/utils/i18n';
import {
  searchOrganizationMembers,
  useCombinedQueriesTeamGeneralInfo,
  useWorkspaceId,
} from '@heidi/workspace/data';

const useActiveTeamId = (): string | undefined => {
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  return teamDetails?.team?.id;
};

const useActiveTeamContext = () => {
  const { teamDetails, isJoinedTeamsFetched, isLoadingTeamDetails, hasTeam } =
    useCombinedQueriesTeamGeneralInfo();
  const teamId = teamDetails?.team?.id;
  const isResolving = !isJoinedTeamsFetched || (hasTeam && isLoadingTeamDetails);
  const hasNoTeam = isJoinedTeamsFetched && !hasTeam;
  return { teamId, isResolving, hasNoTeam };
};

/**
 * Opens `GET .../problem-set/stream` and forwards its frames. Named, so the
 * wiring suite can pin it by name the way it pins the hooks — and named
 * differently from the port it fills, because the guide generator reads the
 * seam's ports off `key: value` pairs and a shorthand property would drop this
 * one from every integrations guide. `onerror` is reported and then rethrown —
 * the rethrow is what makes the SSE client stop retrying and close; the
 * rejected promise is then absorbed here, since the failure has already been
 * delivered through `onError`.
 */
const openProblemSetStream: SubscribeToProblemSetStream = ({
  sessionId,
  signal,
  onEvent,
  onClose,
  onError,
}) => {
  const url = `${getDomain('v2')}sessions/${encodeURIComponent(sessionId)}/problem-set/stream`;
  fetchEventSource(url, {
    method: 'GET',
    headers: buildScribeSseHeaders(getAuthTokenForHttpClientSync()),
    signal,
    openWhenHidden: true,
    onmessage: (ev) => onEvent({ event: ev.event, data: ev.data }),
    onclose: onClose,
    onerror: (error: unknown) => {
      onError(error);
      throw error;
    },
  }).catch(() => undefined);
};

/**
 * Loader for the clinical-coding service client the problem typeahead reads.
 * The seam's port type admits `get` only, which is the narrowing; the object
 * is this app's own client for SCC's public catalogue routes (a plain Heidi
 * bearer, not the coding package's minted case-route token). A
 * dynamic import for the same reason as `loadInboxHttp`: this module is
 * imported for effect by `_app`, and the client's cone must not join it at
 * module scope.
 */
const loadClinicalCodingHttpClient = () =>
  import('@/features/integrations-ehr/problem-charting/api/sccPublicHttp').then(
    (m) => m.sccPublicHttp
  );

/**
 * Loader for the app's unauthenticated template-gallery client, read by
 * `./ehr`'s country-scoped provider list.
 */
const loadTemplateGalleryHttpClient = () =>
  import('@/utils/globalHTTP').then((m) => m.globalHttp);

/**
 * Member search for the inbox assignee picker, mapped to the `user_ref_id`-keyed
 * candidate inbox-service assigns on. Members without a `user_ref_id` are
 * dropped: the assignee record cannot reference them. The label is name, else
 * email — the same fallback inbox-service applies when it snapshots the name,
 * and a term ml-scribe's tokenised search matches when the server verifies
 * the choice by searching it back.
 */
const searchAssigneeCandidates = async (q: string): Promise<InboxAssigneeCandidate[]> => {
  const { data } = await searchOrganizationMembers({ q, limit: 20 });
  const locale = typeof navigator === 'undefined' ? undefined : navigator.language;
  return data.members.flatMap((member) => {
    if (!member.user_ref_id) return [];
    const name = formatCjkAwareDisplayName(
      { firstName: member.first_name, lastName: member.last_name },
      locale
    );
    const displayName = name || member.email || '';
    if (!displayName) return [];
    return [
      {
        user_ref_id: member.user_ref_id,
        display_name: displayName,
        avatar_url: member.profile_picture_url ?? null,
      },
    ];
  });
};

configureIntegrationsData({
  loadInboxHttp: () =>
    import('@/features/document-inbox/api/inboxHttp').then((m) => m.inboxHttp),
  useWorkspaceId,
  onOnboardingSessionCleared: clearIntegrationOnboardingSession,
  useTeamId: useActiveTeamId,
  useTeamContext: useActiveTeamContext,
  // The `./problem-charting` door's module-scope invalidator runs against the
  // app singleton — the same instance `coding-data-wiring.ts` injects.
  queryClient: reactQueryClient,
  // And its live feed: the SSE transport the package cannot build itself.
  subscribeToProblemSetStream: openProblemSetStream,
  loadClinicalCodingHttp: loadClinicalCodingHttpClient,
  loadTemplateGalleryHttp: loadTemplateGalleryHttpClient,
  searchAssigneeCandidates,
});
